import { ContestType } from '@betterpool/api-types/contests-service';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { SlateStatus } from '@betterpool/api-types/games-service';

import { findDefaultSlate } from '~/domains/games/services/find-default-slate.utils';
import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import { useContestSlates } from '~/domains/contest/hooks/useContestSlates';

import { useContestContext } from '../ContestContext/ContestContext';
import type { SlateWithStateAndSettingsTeamSurvivor } from '../../../teamSurvivor/parsers/parseSlatesTeamSurvivor';
import type { SlateWithStateAndSettingsTeamPickEm } from '../../../teamPickEm/parsers/parseSlatesTeamPickEm';
import type { SlateWithStateAndSettingsPlayerTier } from '../../../playerTier/parsers/parseSlatesPlayerTier';
import type { SlateWithStateAndSettingsPlayerSurvivor } from '../../../playerSurvivor/parsers/parsePlayerSurvivorSlates';

import type { SlateWithStateAndSettingsUnknownType } from './parseSlates/parseSlates';
import parseSlates from './parseSlates/parseSlates';

// Loading state constants
const LOADING_SLATE_BASE = {
  contestType: null,
  slates: [],
  state: {
    isAfterPickDeadline: false,
    isGenerated: false,
  },
  settings: {
    expectedPicksCount: 0,
  },
} as const;

const LOADING_SLATE_DEFAULT = {
  ...LOADING_SLATE_BASE,
  id: null,
  slate: {},
} as const;

const LOADING_SLATE_WITH_PICKSHEET_ACCESS = {
  ...LOADING_SLATE_BASE,
  slate: {
    isPicksheetAvailable: true,
  },
} as const;

type SlatesContextValueBase = {
  setCurrentSlateId: (newSlateId: string) => void;
  expectedPicksCount: number;
};

type SlatesContextValueUnkownType = SlatesContextValueBase & {
  contestType: null;
  slates: SlateWithStateAndSettingsUnknownType[];
  currentSlate: SlateWithStateAndSettingsUnknownType | null;
  defaultSlate: SlateWithStateAndSettingsUnknownType | null;
  defaultStandingsSlate: SlateWithStateAndSettingsUnknownType | null;
};

type SlatesContextValuePlayerTier = SlatesContextValueBase & {
  contestType: ContestType.PLAYER_TIER | ContestType.PLAYER_ONE_AND_DONE;
  slates: SlateWithStateAndSettingsPlayerTier[];
  currentSlate: SlateWithStateAndSettingsPlayerTier | null;
  defaultSlate: SlateWithStateAndSettingsPlayerTier | null;
  defaultStandingsSlate: SlateWithStateAndSettingsPlayerTier | null;
};

type SlatesContextValueTeamSurvivor = SlatesContextValueBase & {
  contestType: ContestType.TEAM_SURVIVOR;
  slates: SlateWithStateAndSettingsTeamSurvivor[];
  currentSlate: SlateWithStateAndSettingsTeamSurvivor | null;
  defaultSlate: SlateWithStateAndSettingsTeamSurvivor | null;
  defaultStandingsSlate: SlateWithStateAndSettingsTeamSurvivor | null;
};

type SlatesContextValueTeamPickEm = SlatesContextValueBase & {
  contestType: ContestType.TEAM_PICK_EM;
  slates: SlateWithStateAndSettingsTeamPickEm[];
  currentSlate: SlateWithStateAndSettingsTeamPickEm | null;
  defaultSlate: SlateWithStateAndSettingsTeamPickEm | null;
  defaultStandingsSlate: SlateWithStateAndSettingsTeamPickEm | null;
};

type SlatesContextValuePlayerSurvivor = SlatesContextValueBase & {
  contestType: ContestType.PLAYER_SURVIVOR;
  // The reason why we have SlateWithStateAndSettingsUnknownType is cause we use base parser for player survivor
  slates: SlateWithStateAndSettingsPlayerSurvivor[];
  currentSlate: SlateWithStateAndSettingsPlayerSurvivor | null;
  defaultSlate: SlateWithStateAndSettingsPlayerSurvivor | null;
  defaultStandingsSlate: SlateWithStateAndSettingsPlayerSurvivor | null;
};

type SlatesContextValueTypes = {
  [ContestType.PLAYER_TIER]: SlatesContextValuePlayerTier;
  [ContestType.PLAYER_ONE_AND_DONE]: SlatesContextValuePlayerTier;
  [ContestType.TEAM_SURVIVOR]: SlatesContextValueTeamSurvivor;
  [ContestType.TEAM_PICK_EM]: SlatesContextValueTeamPickEm;
  [ContestType.PLAYER_SURVIVOR]: SlatesContextValuePlayerSurvivor;
};

type SlatesContextValue =
  | SlatesContextValueUnkownType
  | SlatesContextValuePlayerTier
  | SlatesContextValueTeamSurvivor
  | SlatesContextValueTeamPickEm
  | SlatesContextValuePlayerSurvivor;

const SlatesContext = createContext<SlatesContextValue>({
  setCurrentSlateId: () => {},
  contestType: null,
  slates: [],
  currentSlate: null,
  defaultSlate: null,
  defaultStandingsSlate: null,
  expectedPicksCount: 1,
});

function SlatesContextProvider({
  children,
  ignoreQuery,
  isStandings,
}: React.PropsWithChildren<{ ignoreQuery?: boolean; isStandings?: boolean }>) {
  const router = useRouter();

  const { slateId } = router.query;
  const { contest } = useContestContext();
  const { data: slates, isLoading: isSlatesLoading } = useContestSlates(contest?.id);

  const slatesWithStateAndSettings = useMemo(() => {
    if (!contest || isSlatesLoading) {
      return [];
    }

    return parseSlates(contest, slates);
  }, [contest, slates, isSlatesLoading]);

  const defaultSlate = useMemo(() => {
    if (!contest) {
      return null;
    }

    if (contest.contest_type === ContestType.TEAM_SURVIVOR && contest.final_slate_id) {
      return slatesWithStateAndSettings.find(({ id }) => id === contest.final_slate_id) ?? null;
    }

    const defaultSlateBase = findDefaultSlate<ContestSlate>(slates, true);

    return defaultSlateBase
      ? slatesWithStateAndSettings.find(({ id }) => id === defaultSlateBase.id)
      : null;
  }, [contest, slates, slatesWithStateAndSettings]);

  const defaultStandingsSlate = useMemo(() => {
    if (!contest) {
      return LOADING_SLATE_DEFAULT;
    }

    if (contest.contest_type === ContestType.TEAM_SURVIVOR && contest.final_slate_id) {
      return slatesWithStateAndSettings.find(({ id }) => id === contest.final_slate_id) ?? null;
    }

    return (
      slatesWithStateAndSettings.findLast(({ slate }) => slate.status !== SlateStatus.SCHEDULED) ??
      slatesWithStateAndSettings[0] ??
      null
    );
  }, [contest, slatesWithStateAndSettings]);

  const currentSlate = useMemo(() => {
    if (!contest) {
      return LOADING_SLATE_DEFAULT;
    }

    // If we have a slateId in URL, prioritize it and wait for slates to load
    if (slateId) {
      if (isSlatesLoading) {
        // Return loading state but preserve the slateId
        return {
          ...LOADING_SLATE_WITH_PICKSHEET_ACCESS,
          id: slateId,
        };
      }

      // Find the slate matching the URL slateId
      const urlSlate = slatesWithStateAndSettings.find(({ id }) => id === slateId);
      if (urlSlate) {
        return urlSlate;
      }

      // If URL slateId doesn't match any slate, fall back to default
      return defaultSlate;
    }

    // No slateId in URL, use default behavior
    if (isSlatesLoading) {
      return {
        ...LOADING_SLATE_WITH_PICKSHEET_ACCESS,
        id: null,
      };
    }

    return isStandings ? defaultStandingsSlate : defaultSlate;
  }, [
    contest,
    defaultSlate,
    defaultStandingsSlate,
    isStandings,
    isSlatesLoading,
    slateId,
    slatesWithStateAndSettings,
  ]);

  const setCurrentSlateId = useCallback(
    (newSlateId: string) => {
      if (ignoreQuery) return;

      router.replace({
        query: { ...router.query, slateId: newSlateId, isEdit: undefined },
      });
    },
    [ignoreQuery, router]
  );

  useEffect(() => {
    if (!currentSlate) {
      return;
    }

    if (currentSlate?.id !== slateId) {
      setCurrentSlateId(currentSlate.id);
    }
  }, [currentSlate, router, setCurrentSlateId, slateId]);

  const expectedPicksCount = useMemo(() => {
    if (contest?.settings?.stepUp?.slates?.length > 0 && currentSlate?.id) {
      const slateConfig = contest.settings.stepUp.slates.find(
        (slateSettings) => slateSettings.slateId === currentSlate.id
      );
      if (slateConfig && typeof slateConfig.expectedPicksCount === 'number') {
        return slateConfig.expectedPicksCount;
      }
    }

    if (currentSlate?.settings?.expectedPicksCount) {
      return currentSlate.settings.expectedPicksCount;
    }

    return contest?.settings?.expectedPicksCount ?? 1;
  }, [
    contest?.settings?.expectedPicksCount,
    currentSlate?.id,
    contest?.settings?.stepUp?.slates,
    currentSlate?.settings?.expectedPicksCount,
  ]);

  const value = useMemo(() => {
    if (!contest) {
      return {
        contestType: null,
        slates: [],
        currentSlate: null,
        defaultSlate: null,
        defaultStandingsSlate: null,
        setCurrentSlateId,
        expectedPicksCount: 1,
      } satisfies SlatesContextValueUnkownType;
    }

    return {
      contestType: contest.contest_type,
      slates: slatesWithStateAndSettings,
      currentSlate,
      defaultSlate,
      defaultStandingsSlate,
      setCurrentSlateId,
      expectedPicksCount,
    } as SlatesContextValue;
  }, [
    contest,
    currentSlate,
    defaultSlate,
    defaultStandingsSlate,
    setCurrentSlateId,
    slatesWithStateAndSettings,
    expectedPicksCount,
  ]);

  return <SlatesContext.Provider value={value}>{children}</SlatesContext.Provider>;
}

const isContestTypeSupported = (
  contestTypes: ContestType[]
): contestTypes is (keyof SlatesContextValueTypes)[] =>
  contestTypes.every((contestType) =>
    [
      ContestType.PLAYER_TIER,
      ContestType.PLAYER_ONE_AND_DONE,
      ContestType.TEAM_SURVIVOR,
      ContestType.TEAM_PICK_EM,
      ContestType.PLAYER_SURVIVOR,
    ].includes(contestType)
  );

function useSlatesContext(): SlatesContextValueUnkownType;
function useSlatesContext<CT extends keyof SlatesContextValueTypes>(
  contestTypes: CT[]
): SlatesContextValueTypes[CT];
function useSlatesContext(
  contestTypes?: (keyof SlatesContextValueTypes)[]
): SlatesContextValueTypes[(typeof contestTypes)[number]] | SlatesContextValueUnkownType {
  const context = useContext(SlatesContext);

  if (!context) {
    throw new Error('useSlatesContext must be used within a SlatesContextProvider');
  }

  if (context.contestType && contestTypes && !contestTypes.includes(context.contestType)) {
    throw new Error(
      `useSlatesContext must be used with the correct contest type. Used '${contestTypes.join(
        "' | '"
      )}' instead of '${context.contestType}'`
    );
  }

  if (!contestTypes || isContestTypeSupported(contestTypes)) {
    return context as SlatesContextValueTypes[(typeof contestTypes)[number]];
  }

  return context as SlatesContextValueUnkownType;
}

export { SlatesContextProvider, useSlatesContext };
